import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useBatch } from '../../store/batchStore';
import { probeWorker } from '../../bulk/probe';
import { clamp } from '../../core/math';
import { BatchConfigPanel } from './BatchConfigPanel';
import { BatchGallery } from './BatchGallery';
import { BatchExportBar } from './BatchExportBar';
import { CompareView } from './CompareView';
import { VariantDetail } from './VariantDetail';

export function BatchStudio(): JSX.Element {
  const variants = useBatch((s) => s.variants);
  const detailId = useBatch((s) => s.detailId);
  const setDetail = useBatch((s) => s.setDetail);
  const [panelWidth, setPanelWidth] = useState(320);
  const [capabilityWarning, setCapabilityWarning] = useState<string | null>(null);

  // Check once what a worker on this browser can actually do, rather than assuming.
  useEffect(() => {
    let cancelled = false;
    void probeWorker().then((caps) => {
      if (cancelled) return;
      if (!caps) {
        setCapabilityWarning('Web Worker konnten nicht gestartet werden — der Batch-Modus braucht sie.');
        return;
      }
      const missing = Object.entries(caps)
        .filter(([, ok]) => !ok)
        .map(([name]) => name);
      if (missing.length) {
        setCapabilityWarning(`Dieser Browser unterstützt im Worker nicht: ${missing.join(', ')}.`);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const detail = variants.find((v) => v.id === detailId) ?? null;

  return (
    <div className="flex min-h-0 flex-1">
      <aside className="shrink-0 overflow-y-auto border-r border-ink-800 bg-ink-900" style={{ width: panelWidth }}>
        {capabilityWarning && (
          <div className="m-3 flex gap-2 rounded-md border border-amber-900/50 bg-amber-950/40 p-2 text-[10px] leading-relaxed text-amber-200">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {capabilityWarning}
          </div>
        )}
        <BatchConfigPanel />
      </aside>

      <Resizer width={panelWidth} onResize={(dx, start) => setPanelWidth(clamp(start + dx, 260, 520))} />

      <div className="flex min-w-0 flex-1 flex-col">
        <BatchGallery />
        <CompareView />
        <BatchExportBar />
      </div>

      {detail && <VariantDetail variant={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Resizer({
  width,
  onResize,
}: {
  width: number;
  onResize: (dx: number, startWidth: number) => void;
}): JSX.Element {
  const [drag, setDrag] = useState<{ x: number; width: number } | null>(null);
  return (
    <div
      className="w-[3px] shrink-0 cursor-col-resize bg-ink-800 transition-colors hover:bg-ink-600"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag({ x: e.clientX, width });
      }}
      onPointerMove={(e) => drag && onResize(e.clientX - drag.x, drag.width)}
      onPointerUp={() => setDrag(null)}
    />
  );
}
